/**
 * Serviço para busca de CEP/ZIP Code
 * Suporta CEP brasileiro (ViaCEP) e ZIP Code americano (Zippopotam.us)
 */

interface BrazilianCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

interface AmericanZipResponse {
  'post code': string;
  country: string;
  'country abbreviation': string;
  places: Array<{
    'place name': string;
    longitude: string;
    state: string;
    'state abbreviation': string;
    latitude: string;
  }>;
}

export interface AddressData {
  address: string;
  city: string;
  state: string;
  zip_code: string;
}

/**
 * Detecta se o código é CEP brasileiro ou ZIP Code americano
 */
export const detectPostalCodeType = (code: string): 'BR' | 'US' | null => {
  const cleanCode = code.replace(/\D/g, '');
  if (cleanCode.length === 8) return 'BR'; // CEP brasileiro: 8 dígitos
  if (cleanCode.length === 5 || cleanCode.length === 9 || cleanCode.length === 10) return 'US'; // ZIP Code: 5, 9 ou 10 dígitos
  return null;
};

/**
 * Busca endereço por CEP brasileiro (ViaCEP)
 */
const fetchBrazilianCep = async (cep: string): Promise<AddressData> => {
  const cleanCep = cep.replace(/\D/g, '');
  
  // Garantir que o CEP tem 8 dígitos
  if (cleanCep.length !== 8) {
    throw new Error('CEP deve ter 8 dígitos');
  }
  
  console.log('🔍 Buscando CEP brasileiro:', cleanCep);
  const url = `https://viacep.com.br/ws/${cleanCep}/json/`;
  console.log('🔍 URL:', url);
  
  const response = await fetch(url);
  
  if (!response.ok) {
    console.error('❌ Erro HTTP ao buscar CEP:', response.status, response.statusText);
    throw new Error(`Erro ao buscar CEP: ${response.statusText}`);
  }
  
  const data: BrazilianCepResponse = await response.json();
  console.log('📋 Resposta ViaCEP:', data);
  
  // ViaCEP retorna { erro: true } quando não encontra
  if (data.erro === true) {
    console.error('❌ CEP não encontrado (erro: true):', data);
    throw new Error('CEP não encontrado');
  }
  
  // Verificar se tem logradouro (alguns CEPs podem não ter logradouro mas ter cidade/estado)
  if (!data.logradouro && !data.localidade) {
    console.error('❌ CEP sem dados válidos:', data);
    throw new Error('CEP não encontrado ou sem dados disponíveis');
  }
  
  // Construir endereço mesmo se logradouro estiver vazio
  let address = data.logradouro || '';
  if (data.bairro) {
    address += address ? `, ${data.bairro}` : data.bairro;
  }
  
  const result: AddressData = {
    address: address || '', // Sempre retorna string, mesmo que vazia
    city: data.localidade || '',
    state: data.uf || '',
    zip_code: cleanCep
  };
  
  console.log('✅ Endereço extraído:', result);
  return result;
};

/**
 * Busca endereço por ZIP Code americano (Zippopotam.us)
 */
const fetchAmericanZipCode = async (zipCode: string): Promise<AddressData> => {
  const cleanZip = zipCode.replace(/\D/g, '').substring(0, 5);
  
  if (cleanZip.length !== 5) {
    throw new Error('ZIP Code deve ter 5 dígitos');
  }
  
  const response = await fetch(`https://api.zippopotam.us/us/${cleanZip}`);
  
  if (!response.ok) {
    throw new Error('ZIP Code não encontrado');
  }
  
  const data: AmericanZipResponse = await response.json();
  
  if (!data.places || data.places.length === 0) {
    throw new Error('ZIP Code não encontrado');
  }
  
  const place = data.places[0];
  
  return {
    address: place['place name'],
    city: place['place name'],
    state: place['state abbreviation'],
    zip_code: cleanZip
  };
};

/**
 * Função unificada para buscar endereço por CEP/ZIP Code
 */
export const fetchAddressByCep = async (code: string): Promise<AddressData> => {
  const cleanCode = code.replace(/\D/g, '');
  const detectedType = detectPostalCodeType(cleanCode);
  
  if (!detectedType) {
    throw new Error('CEP/ZIP Code inválido');
  }
  
  try {
    if (detectedType === 'BR') {
      return await fetchBrazilianCep(cleanCode);
    } else {
      return await fetchAmericanZipCode(cleanCode);
    }
  } catch (error: any) {
    throw new Error(error.message || 'Erro ao buscar CEP/ZIP Code');
  }
};

